import { TranslationsApiService } from 'src/app/builder-services/api/translations.api.service'
import { Injectable } from '@angular/core'
import { BuilderService } from '../builder-services/builder.service'

@Injectable({
  providedIn: 'root'
})
export class TranslationService {
  constructor(
    private BuilderService: BuilderService,
    private translationsApi: TranslationsApiService,
  ) {}

  private get projectId(): string {
    return String(this.BuilderService.selectedProject)
  }

  // ── Languages and config ─────────────────────────────────────

  getProjectLanguages(): Promise<string[]> {
    return this.translationsApi.getProjectLanguages(this.projectId)
  }

  addProjectLanguage(language: string): Promise<void> {
    return this.translationsApi.addProjectLanguage(this.projectId, language)
  }

  getTranslationConfig(): Promise<{ defaultLanguage: string; translateNumbers: boolean } | null> {
    return this.translationsApi.getTranslationConfig(this.projectId)
  }

  upsertTranslationConfig(config: { defaultLanguage: string; translateNumbers: boolean }): Promise<void> {
    return this.translationsApi.upsertTranslationConfig(this.projectId, config)
  }

  /**
   * Brings the project's language list in line with `languages`, removing the
   * ones in `toDelete` first. Sequential on purpose — the ordering is what the
   * multilingual screen relies on.
   */
  async syncProjectLanguages(languages: string[], toDelete: string[]): Promise<void> {
    for (const lang of toDelete) {
      await this.translationsApi.deleteProjectLanguage(this.projectId, lang)
    }
    for (const lang of languages) {
      await this.translationsApi.addProjectLanguage(this.projectId, lang)
    }
  }

  // ── Strings ──────────────────────────────────────────────────

  getAllTranslationsForLanguage(language: string): Promise<Record<string, { value: string }>> {
    return this.translationsApi.getAllTranslationsForLanguage(this.projectId, language)
  }

  upsertTranslations(language: string, translations: Record<string, string>): Promise<void> {
    return this.translationsApi.upsertTranslations(this.projectId, language, translations)
  }

  /** Remove duplicates while preserving the original order. */
  public removeDuplicatesPreserveOrder(arr: string[]): string[] {
    const seen = new Set<string>()
    const out: string[] = []
    for (const s of arr) {
      if (!seen.has(s)) {
        seen.add(s)
        out.push(s)
      }
    }
    return out
  }

  /** Extract plain-text strings from "text" blocks (HTML stripped). */
  public extractBlockTexts(textBlocks: any): string[] {
    if (!textBlocks) return []
    const out: string[] = []
    for (const k of Object.keys(textBlocks)) {
      const allBlocks = textBlocks[k]?.allBlocks
      if (!Array.isArray(allBlocks)) continue
      for (const b of allBlocks) {
        if (!b || b.type !== 'text') continue
        const t = this.extractTextFromHTML(b.data?.value)?.trim()
        if (t) out.push(t)
      }
    }
    return out
  }
  /*
   * extractTextFromHTML: Helper function to extract plain text from HTML strings.
   * - Uses DOMParser to parse the HTML content and extract the text content.
   */
  extractTextFromHTML(html: string): string {
    const parser = new DOMParser()
    const doc = parser.parseFromString(html, 'text/html')

    // Select all <p> elements
    const pElements = doc.querySelectorAll('p')

    // Loop through and remove <p> that contain only <br> or are empty
    pElements.forEach((p) => {
      if (p.innerHTML.trim() === '<br>' || p.innerHTML.trim() === '') {
        p.remove()
      }
    })

    // Extract text content of remaining <p> tags, filtering out empty strings
    const paragraphs = Array.from(doc.querySelectorAll('p'))
      .map((p) => p.textContent?.trim() || '')
      .filter((text) => text !== '') // Filter out empty strings

    return paragraphs.join(' ').trim()
  }

  /**
   * Extracts product variation values and attribute names from the given products array.
   *
   * @param productsObject Array of product objects returned from the productService.
   * @returns An object containing arrays of productVariationValues and productAttributeNames.
   */
  public extractProductData(productsObject: any[]): {
    productVariationValues: string[]
    productAttributeNames: string[]
    productNames: string[]
    productDescription: string[]
    tagCategories: string[]
    tagValues: string[]
    productBadges: string[]
  } {
    const productVariationValues: string[] = []
    const productAttributeNames: string[] = []
    const productNames: string[] = []
    const productDescription: string[] = []
    const tagCategories: string[] = []
    const tagValues: string[] = []
    const productBadges: string[] = []
    if (!Array.isArray(productsObject) && typeof productsObject !== 'object') {
      return {
        productVariationValues,
        productAttributeNames,
        productNames,
        productDescription,
        tagCategories,
        tagValues,
        productBadges
      }
    }
    const products = Array.isArray(productsObject) ? productsObject : Object.values(productsObject)
    products.forEach((product: any) => {
      if (product?.productName && typeof product.productName === 'string' && product.productName.trim().length > 0) {
        productNames.push(product.productName.trim())
      }
      if (product?.badge && typeof product.badge === 'string' && product.badge.trim().length > 0) {
        productBadges.push(product.badge.trim())
      }
      if (product?.productVariations && Array.isArray(product.productVariations)) {
        product.productVariations.forEach((variation: any) => {
          if (variation?.values && typeof variation.values === 'string' && variation.values.trim().length > 0) {
            const trimmedVal = variation.values.trim()
            if (trimmedVal.includes(',')) {
              trimmedVal.split(',').forEach((val: string) => {
                productVariationValues.push(val.trim())
              })
            } else {
              productVariationValues.push(trimmedVal)
            }
          }
        })
      }
      if (product?.productAttributes && Array.isArray(product.productAttributes)) {
        product.productAttributes.forEach((attr: any) => {
          if (attr?.name && typeof attr.name === 'string') {
            productAttributeNames.push(attr.name)
          }
        })
      }
      if (product?.tags && typeof product.tags === 'object' && product.tags !== null) {
        Object.keys(product.tags).forEach((category) => {
          if (category && typeof category === 'string' && category.trim().length > 0) {
            tagCategories.push(category.trim())
            const tagArray = product.tags[category]
            if (Array.isArray(tagArray)) {
              tagArray.forEach((tag: any) => {
                if (tag && typeof tag === 'string' && tag.trim().length > 0) {
                  tagValues.push(tag.trim())
                }
              })
            }
          }
        })
      }
      if (product?.productDescription) {
        const paragraphs = this.extractParagraphTexts(product.productDescription)
        productDescription.push(...paragraphs)
      }
    })

    return {
      productVariationValues,
      productAttributeNames,
      productNames: Array.from(new Set(productNames)),
      productDescription: Array.from(new Set(productDescription)),
      tagCategories: Array.from(new Set(tagCategories)),
      tagValues: Array.from(new Set(tagValues)),
      productBadges: Array.from(new Set(productBadges))
    }
  }

  /**
   * Extracts textual content (including button text) from section blocks.
   *
   * @param blocksData The array of blocks from the blockDataService.
   */
  public extractSectionTexts(blocksData: any[]): string[] {
    return blocksData
      .filter((block: any) => block.type === 'section')
      .flatMap((block: any) => {
        return block.elements.flatMap((column: any) => {
          return column.elements.map((element: any) => {
            const btnText = element.data?.btnText || ''
            const value = element.data?.value ? this.extractTextFromHTML(element.data.value) : ''
            // Filter out empty strings
            return [btnText, value].filter(Boolean)
          })
        })
      })
  }

  /**
   * Retrieves menu items from the BuilderService global footer data.
   */
  public getMenuItems(): string[] {
    return (this.BuilderService.globalFooter?.data?.menuItems as { menuText: string }[] | undefined)?.map((item) => item?.menuText) || []
  }

  /**
   * Extracts button texts from any blocks of type "button".
   *
   * @param blocksData The array of blocks from the blockDataService.
   */
  public extractButtonTexts(blocksData: any[]): string[] {
    return blocksData.filter((block: any) => block?.type === 'button' && block?.data?.btnText).map((block: any) => block?.data?.btnText) || []
  }

    /**
   * Extracts text from header blocks (nav items, login/account).
   */
  public extractHeaderTexts(blocksData: any[]): string[] {
    const texts: string[] = []
    blocksData.forEach((block) => {
      if (block.type === 'header' && block.data) {
        if (block.data.userName) texts.push(block.data.userName)
        if (Array.isArray(block.data.navItems)) {
            block.data.navItems.forEach((item: any) => {
                if (item.label) texts.push(item.label)
            })
        }
      }
    })
    return texts
  }

  /**
   * Extracts text from footer blocks (links, description, payment methods).
   */
  public extractFooterTexts(blocksData: any[]): string[] {
    const texts: string[] = []
    blocksData.forEach((block) => {
        if (block.type === 'footer' && block.data) {
            if (block.data.description) texts.push(block.data.description)
            if (Array.isArray(block.data.companyLinks)) {
                block.data.companyLinks.forEach((link: any) => {
                    if (link.name) texts.push(link.name)
                })
            }
            if (Array.isArray(block.data.productLinks)) {
                block.data.productLinks.forEach((link: any) => {
                    if (link.name) texts.push(link.name)
                })
            }
            if (Array.isArray(block.data.paymentMethods)) {
                 block.data.paymentMethods.forEach((pm: any) => {
                     if (pm.name) texts.push(pm.name)
                 })
            }
        }
    })
    return texts
  }
  
  /**
   * Extracts individual text nodes from HTML, returning each as a separate string.
   * Preserves paragraph granularity so each text fragment can be a translation key.
   */
  public extractParagraphTexts(html: string): string[] {
    const normalized = html.replace(/<br\s*\/?>/gi, '\n')
    const doc = new DOMParser().parseFromString(normalized, 'text/html')

    return (doc.body.textContent || '')
      .split('\n')
      .map((text) => text.replace(/[\x00-\x1F\x7F]+/g, ' ').trim())
      .filter((text) => text.length > 0)
  }

  public detectLanguageFromText(text: string): string {
    const t = (text || '').trim()
    if (!t) return 'Unknown'

    // Detect main script blocks
    const hasLatin = /[A-Za-z]/.test(t)
    const hasArabic = /[\u0600-\u06FF]/.test(t)
    const hasHebrew = /[\u0590-\u05FF]/.test(t)
    const hasCyrillic = /[\u0400-\u04FF]/.test(t)
    const hasHangul = /[\uAC00-\uD7AF]/.test(t)
    const hasKana = /[\u3040-\u30FF]/.test(t)
    const hasHan = /[\u4E00-\u9FFF]/.test(t)
    const hasDevanagari = /[\u0900-\u097F]/.test(t)
    const hasTamil = /[\u0B80-\u0BFF]/.test(t)
    const hasTelugu = /[\u0C00-\u0C7F]/.test(t)
    const hasKannada = /[\u0C80-\u0CFF]/.test(t)
    const hasMalayalam = /[\u0D00-\u0D7F]/.test(t)
    const hasSinhala = /[\u0D80-\u0DFF]/.test(t)
    const hasThai = /[\u0E00-\u0E7F]/.test(t)
    const hasLao = /[\u0E80-\u0EFF]/.test(t)
    const hasKhmer = /[\u1780-\u17FF]/.test(t)
    const hasTigrinya = /[\u1200-\u137F]/.test(t)
    const hasThaana = /[\u0780-\u07BF]/.test(t)

    // Arabic-script languages to force as Arabic
    const forceArabic = new Set(['Persian', 'Urdu', 'Pashto', 'Sindhi', 'Dhivehi', 'Uighur'])

    // Arabic block detection
    if (hasArabic) {
      if (/[گپچژ]/.test(t)) return 'Persian'
      if (/[ےۃ۔]/.test(t)) return 'Urdu'
      if (/[پځښګڼۍ]/.test(t)) return 'Pashto'
      if (/[۽ۀ]/.test(t)) return 'Sindhi'
      if (/[ހ-ޯ]/.test(t)) return 'Dhivehi'
      if (/[ئۇۋ]/.test(t)) return 'Uighur'
      return 'Arabic'
    }

    // Other script detections
    if (hasHebrew) return 'Hebrew'
    if (hasThaana) return 'Dhivehi'
    if (hasSinhala) return 'Sinhala'
    if (hasTamil) return 'Tamil'
    if (hasTelugu) return 'Telugu'
    if (hasKannada) return 'Kannada'
    if (hasMalayalam) return 'Malayalam'
    if (hasThai) return 'Thai'
    if (hasLao) return 'Lao'
    if (hasKhmer) return 'Khmer'
    if (hasTigrinya) return 'Tigrinya'
    if (hasHangul) return 'Korean'
    if (hasKana) return 'Japanese'
    if (hasHan) return 'Chinese'
    if (hasCyrillic) {
      if (/[ІЇЄҐіїєґ]/.test(t)) return 'Ukrainian'
      return 'Russian'
    }

    // Latin by accents
    if (/[ĂÂÎȘȚăâîșț]/.test(t)) return 'Romanian'
    if (/[ÇĞİÖŞÜçğıöşü]/.test(t)) return 'Turkish'
    if (/[ÀÂÆÇÉÈÊËÏÎÔŒÙÛÜŸàâæçéèêëïîôœùûüÿ]/.test(t)) return 'French'
    if (/[ÁÉÍÑÓÚÜáéíñóúü]/.test(t)) return 'Spanish'
    if (/[ÃÕÂÊÔÁÉÍÓÚÀÇãõâêôáéíóúàç]/.test(t)) return 'Portuguese'
    if (/[ÄÖÜäöüß]/.test(t)) return 'German'
    if (/[ÅÄÖåäö]/.test(t)) return 'Swedish'
    if (/[ÆØÅæøå]/.test(t)) return 'Norwegian'
    if (/[ÁČĎÉĚÍŇÓŘŠŤÚŮÝŽáčďéěíňóřšťúůýž]/.test(t)) return 'Czech'
    if (/[ÁČĎÉÍĹĽŇÓŔŠŤÚÝŽáčďéíĺľňóŕšťúýž]/.test(t)) return 'Slovak'
    if (/[ČŠŽčšž]/.test(t) && /[Đđ]/.test(t)) return 'Croatian'
    if (/[ČŠŽčšž]/.test(t) && !/[Đđ]/.test(t)) return 'Slovenian'
    if (/[ĀČĒĢĪĶĻŅŠŪŽāčēģīķļņšūž]/.test(t)) return 'Latvian'
    if (/[ĄČĘĖĮŠŲŪŽąčęėįšųūž]/.test(t)) return 'Lithuanian'
    if (/[ÀÈÉÌÍÎÒÓÙÚàèéìíîòóùú]/.test(t)) return 'Italian'

    if (hasLatin && !/[^\u0000-\u007F]/.test(t)) return 'English'
    return 'Unknown'
  }
}
